import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Award, LogOut, Plus, Users, FileText, Settings,
  Trash2, Edit, UserPlus, BarChart3, Copy, Archive, RotateCcw, Eye, Building2, Mail, Download, Palette, Calculator
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import FormBuilder from '@/components/FormBuilder';
import TemplateManager from '@/components/TemplateManager';
import ApplicationScoring from '@/components/ApplicationScoring';

import AdminOverviewTab from './admin/AdminOverviewTab';
import AdminCommitteesTab from './admin/AdminCommitteesTab';
import AdminScholarshipsTab from './admin/AdminScholarshipsTab';
import AdminUsersTab from './admin/AdminUsersTab';
import AdminApplicationsTab from './admin/AdminApplicationsTab';
import AdminSettingsTab from './admin/AdminSettingsTab';
import AdminScholarshipModal from './admin/AdminScholarshipModal';

const DEFAULT_FORM_CONFIG = [
  {
    id: "personal_info",
    title: "Personal Information",
    description: "Basic applicant information",
    enabled: true,
    fields: [
      { id: "first_name", label: "First Name", type: "text", required: true, placeholder: "", options: [], help_text: "" },
      { id: "middle_name", label: "Middle Name(s)", type: "text", required: false, placeholder: "", options: [], help_text: "" },
      { id: "family_name", label: "Family Name", type: "text", required: true, placeholder: "", options: [], help_text: "" },
      { id: "email", label: "Email Address", type: "email", required: true, placeholder: "", options: [], help_text: "" },
      { id: "father_name", label: "Father's Name", type: "text", required: false, placeholder: "", options: [], help_text: "" },
      { id: "mother_name", label: "Mother's Name", type: "text", required: false, placeholder: "", options: [], help_text: "" },
      { id: "ahepa_affiliation", label: "AHEPA Family Affiliation", type: "checkbox_group", required: false, placeholder: "", options: ["AHEPA", "Daughters of Penelope", "Sons of Pericles", "Maids of Athena"], help_text: "Select all that apply" },
      { id: "member_name", label: "Member's Name", type: "text", required: false, placeholder: "", options: [], help_text: "Name of the affiliated member" },
      { id: "hellenic_school", label: "Attended Hellenic School?", type: "radio", required: false, placeholder: "", options: ["Yes", "No"], help_text: "" },
      { id: "hellenic_school_name", label: "Hellenic School Name", type: "text", required: false, placeholder: "", options: [], help_text: "" },
      { id: "hellenic_school_years", label: "Years Completed", type: "text", required: false, placeholder: "", options: [], help_text: "" },
      { id: "hellenic_community", label: "Hellenic Community Member?", type: "radio", required: false, placeholder: "", options: ["Yes", "No"], help_text: "" },
      { id: "hellenic_community_name", label: "Community Member's Name", type: "text", required: false, placeholder: "", options: [], help_text: "" },
      { id: "greek_heritage", label: "Greek Heritage Description", type: "textarea", required: true, placeholder: "Describe how you are a descendant of Greek Heritage", options: [], help_text: "" },
      { id: "long_term_aspirations", label: "Long Term Aspirations", type: "textarea", required: true, placeholder: "What are your long term aspirations?", options: [], help_text: "" }
    ]
  },
  {
    id: "academic_info",
    title: "Academic Information",
    description: "Educational background and achievements",
    enabled: true,
    fields: [
      { id: "high_school_name", label: "High School Name", type: "text", required: true, placeholder: "", options: [], help_text: "" },
      { id: "graduation_date", label: "Graduation Date", type: "date", required: true, placeholder: "", options: [], help_text: "" },
      { id: "current_institution", label: "Current College/University", type: "text", required: true, placeholder: "", options: [], help_text: "" },
      { id: "year_of_study", label: "Year of Study", type: "select", required: true, placeholder: "", options: ["1st Year", "2nd Year", "3rd Year", "4th Year", "5th Year+", "Graduate"], help_text: "" },
      { id: "program", label: "Program/Major", type: "text", required: true, placeholder: "", options: [], help_text: "" },
      { id: "next_year_institution", label: "Next Year Institution", type: "text", required: false, placeholder: "", options: [], help_text: "If different from current" },
      { id: "awards", label: "Scholastic Awards/Honours", type: "array", required: false, placeholder: "", options: [], help_text: "Past 3 years" }
    ]
  },
  {
    id: "activities",
    title: "Activities & Experience",
    description: "Extracurricular, volunteer, and work experience",
    enabled: true,
    fields: [
      { id: "extracurricular", label: "Extracurricular Activities", type: "array", required: false, placeholder: "", options: [], help_text: "Past 3 years" },
      { id: "volunteer", label: "Community & Volunteer Activities", type: "array", required: false, placeholder: "", options: [], help_text: "Past 3 years" },
      { id: "work_experience", label: "Work Experience", type: "array", required: false, placeholder: "", options: [], help_text: "Past 3 years" }
    ]
  },
  {
    id: "sponsor",
    title: "Chapter Endorsement",
    description: "Sponsor endorsement section",
    enabled: true,
    fields: []
  },
  {
    id: "review",
    title: "Review & Submit",
    description: "Review your application before submission",
    enabled: true,
    fields: []
  }
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scholarships, setScholarships] = useState([]);
  const [users, setUsers] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [allApplications, setAllApplications] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showArchived, setShowArchived] = useState(false);
  const [applicationFilter, setApplicationFilter] = useState({ status: '', committee_id: '' });
  
  // Dialog states
  const [scholarshipDialog, setScholarshipDialog] = useState({ open: false, editing: null });
  const [inviteDialog, setInviteDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, scholarship: null });
  const [committeeDialog, setCommitteeDialog] = useState({ open: false, editing: null });
  const [committeeInviteDialog, setCommitteeInviteDialog] = useState({ open: false, committee: null });
  const [committeeMemberLimits, setCommitteeMemberLimits] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, data: null, message: '' });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [importingData, setImportingData] = useState(false);
  const [viewingApplication, setViewingApplication] = useState(null);
  
  // Settings state
  const [scholarshipTypes, setScholarshipTypes] = useState([]);
  const [newScholarshipType, setNewScholarshipType] = useState({ id: '', name: '' });
  
  // Form states
  const [scholarshipForm, setScholarshipForm] = useState({
    name: '',
    description: '',
    amount: 1000,
    start_date: '',
    deadline: '',
    eligibility: '',
    scholarship_type: 'first_year',
    committee_id: null,
    scoring_criteria: [
      { name: 'Academic Merit', weight: 0.3, max_score: 10, description: 'Academic achievements and GPA' },
      { name: 'Community Involvement', weight: 0.25, max_score: 10, description: 'Volunteer and community activities' },
      { name: 'Greek Heritage', weight: 0.2, max_score: 10, description: 'Connection to Greek heritage and AHEPA' },
      { name: 'Leadership', weight: 0.15, max_score: 10, description: 'Leadership roles and potential' },
      { name: 'Aspirations', weight: 0.1, max_score: 10, description: 'Future goals and career plans' }
    ],
    form_config: DEFAULT_FORM_CONFIG,
    is_active: true,
    is_archived: false,
    allow_previous_recipients: true,
    scoring_template: { sections: [] }
  });

  const [editTab, setEditTab] = useState('details');
  
  // Refs for resizable textareas
  const descriptionRef = useRef(null);
  const eligibilityRef = useRef(null);
  
  // Textarea heights state (loaded from localStorage)
  const [textareaHeights, setTextareaHeights] = useState(() => {
    try {
      const saved = localStorage.getItem('scholarship_textarea_heights');
      return saved ? JSON.parse(saved) : { description: 120, eligibility: 120 };
    } catch {
      return { description: 120, eligibility: 120 };
    }
  });
  
  // Save textarea height to localStorage
  const saveTextareaHeight = useCallback((field, height) => {
    setTextareaHeights(prev => {
      const newHeights = { ...prev, [field]: height };
      localStorage.setItem('scholarship_textarea_heights', JSON.stringify(newHeights));
      return newHeights;
    });
  }, []);
  
  // Handle textarea resize
  const handleTextareaResize = useCallback((field) => {
    return (e) => {
      const height = e.target.offsetHeight;
      if (height > 0) {
        saveTextareaHeight(field, height);
      }
    };
  }, [saveTextareaHeight]);
  
  // Apply saved heights when dialog opens
  useEffect(() => {
    if (scholarshipDialog.open) {
      // Apply heights after a short delay to ensure DOM is ready
      setTimeout(() => {
        if (descriptionRef.current) {
          descriptionRef.current.style.height = `${textareaHeights.description}px`;
        }
        if (eligibilityRef.current) {
          eligibilityRef.current.style.height = `${textareaHeights.eligibility}px`;
        }
      }, 100);
    }
  }, [scholarshipDialog.open, textareaHeights]);
  
  const [inviteForm, setInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'chair'
  });

  const [committeeForm, setCommitteeForm] = useState({
    name: '',
    description: '',
    level: 'chapter',
    slug: ''
  });

  const [committeeInviteForm, setCommitteeInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'chair'
  });

  const landingBaseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    fetchData();
    fetchScholarshipTypes();
  }, [showArchived]);

  const fetchData = async () => {
    try {
      const [scholarshipsRes, usersRes, statsRes, committeesRes, applicationsRes] = await Promise.all([
        axios.get(`${API}/scholarships?include_archived=${showArchived}`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/statistics`),
        axios.get(`${API}/committees`),
        axios.get(`${API}/admin/applications?include_archived=${showArchived}`)
      ]);
      setScholarships(scholarshipsRes.data);
      setUsers(usersRes.data);
      setStatistics(statsRes.data);
      setCommittees(committeesRes.data);
      setAllApplications(applicationsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchScholarshipTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/scholarship-types`);
      setScholarshipTypes(response.data);
    } catch (error) {
      // Use defaults if fetch fails
      setScholarshipTypes([
        { id: 'first_year', name: 'First Year' },
        { id: 'continuing', name: 'Continuing Education' },
        { id: 'graduate', name: 'Graduate Studies' }
      ]);
    }
  };
  
  const addScholarshipType = async () => {
    if (!newScholarshipType.id || !newScholarshipType.name) {
      toast.error('Please enter both ID and name for the scholarship type');
      return;
    }
    
    const typeId = newScholarshipType.id.toLowerCase().replace(/\s+/g, '_');
    if (scholarshipTypes.some(t => t.id === typeId)) {
      toast.error('A scholarship type with this ID already exists');
      return;
    }
    
    const updatedTypes = [...scholarshipTypes, { id: typeId, name: newScholarshipType.name }];
    
    try {
      await axios.put(`${API}/settings/scholarship-types`, updatedTypes);
      setScholarshipTypes(updatedTypes);
      setNewScholarshipType({ id: '', name: '' });
      toast.success('Scholarship type added');
    } catch (error) {
      toast.error('Failed to add scholarship type');
    }
  };
  
  const removeScholarshipType = async (typeId) => {
    const updatedTypes = scholarshipTypes.filter(t => t.id !== typeId);
    
    try {
      await axios.put(`${API}/settings/scholarship-types`, updatedTypes);
      setScholarshipTypes(updatedTypes);
      toast.success('Scholarship type removed');
    } catch (error) {
      toast.error('Failed to remove scholarship type');
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleImportPreviewData = async () => {
    if (!window.confirm('Import scholarships and committees from preview environment? Existing data with the same names will be skipped.')) {
      return;
    }
    
    setImportingData(true);
    try {
      const response = await axios.post(`${API}/admin/import-preview-data`);
      const { scholarships_imported, committees_imported, skipped, errors } = response.data;
      
      if (scholarships_imported > 0 || committees_imported > 0) {
        toast.success(`Imported ${committees_imported} committees and ${scholarships_imported} scholarships`);
        fetchData(); // Refresh data
      } else if (skipped?.length > 0) {
        toast.info('All data already exists - nothing to import');
      }
      
      if (errors?.length > 0) {
        toast.error(`Some errors occurred: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to import data');
    } finally {
      setImportingData(false);
    }
  };

  const getDefaultEligibility = (level) => {
    switch (level?.toLowerCase()) {
      case 'national':
        return 'Open to all applicants nationally. Endorsers must be members of the AHEPA or DOP orders.';
      case 'district':
        return 'Open to all applicants within the district. Endorsers must be members of the AHEPA or DOP orders.';
      case 'chapter':
      default:
        return 'Open to anyone in the chapter or near the chapter if there is no chapter in their area. Endorsers must be members of the AHEPA or DOP orders.';
    }
  };

  const openScholarshipDialog = (scholarship = null) => {
    setEditTab('details');
    if (scholarship) {
      setScholarshipForm({
        name: scholarship.name,
        description: scholarship.description,
        amount: scholarship.amount,
        start_date: scholarship.start_date || '',
        deadline: scholarship.deadline?.length === 10 ? `${scholarship.deadline}T23:59` : (scholarship.deadline || ''),
        eligibility: scholarship.eligibility,
        scholarship_type: scholarship.scholarship_type,
        committee_id: scholarship.committee_id || null,
        scoring_criteria: scholarship.scoring_criteria,
        form_config: scholarship.form_config || DEFAULT_FORM_CONFIG,
        is_active: scholarship.is_active,
        is_archived: scholarship.is_archived || false,
        allow_previous_recipients: scholarship.allow_previous_recipients !== false,
        scoring_template: scholarship.scoring_template || { sections: [] }
      });
      setScholarshipDialog({ open: true, editing: scholarship.id });
    } else {
      const today = new Date().toISOString().split('T')[0];
      setScholarshipForm({
        name: '',
        description: '',
        amount: 1000,
        start_date: today,
        deadline: '',
        eligibility: getDefaultEligibility('national'), // Default to national if unassigned, but user can change it
        scholarship_type: 'first_year',
        committee_id: null,
        scoring_criteria: [
          { name: 'Academic Merit', weight: 0.3, max_score: 10, description: 'Academic achievements and GPA' },
          { name: 'Community Involvement', weight: 0.25, max_score: 10, description: 'Volunteer and community activities' },
          { name: 'Greek Heritage', weight: 0.2, max_score: 10, description: 'Connection to Greek heritage and AHEPA' },
          { name: 'Leadership', weight: 0.15, max_score: 10, description: 'Leadership roles and potential' },
          { name: 'Aspirations', weight: 0.1, max_score: 10, description: 'Future goals and career plans' }
        ],
        form_config: DEFAULT_FORM_CONFIG,
        is_active: true,
        is_archived: false,
        allow_previous_recipients: true,
        scoring_template: { sections: [] }
      });
      setScholarshipDialog({ open: true, editing: null });
    }
  };

  const saveScholarship = async (closeAfterSave = true) => {
    try {
      if (scholarshipDialog.editing) {
        await axios.put(`${API}/scholarships/${scholarshipDialog.editing}`, scholarshipForm);
        toast.success('Scholarship saved');
      } else {
        const response = await axios.post(`${API}/scholarships`, scholarshipForm);
        toast.success('Scholarship created');
        // If creating new and not closing, switch to edit mode
        if (!closeAfterSave && response.data?.id) {
          setScholarshipDialog({ open: true, editing: response.data.id });
        }
      }
      if (closeAfterSave) {
        setScholarshipDialog({ open: false, editing: null });
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save scholarship');
    }
  };

  const deleteScholarship = async (id, confirm = false) => {
    try {
      await axios.delete(`${API}/scholarships/${id}?confirm=${confirm}`);
      toast.success('Scholarship permanently deleted');
      setDeleteDialog({ open: false, scholarship: null });
      fetchData();
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('application')) {
        // Has applications - show the delete dialog with options
        const scholarship = scholarships.find(s => s.id === id);
        setDeleteDialog({ open: true, scholarship });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete scholarship');
      }
    }
  };

  const archiveScholarship = async (id) => {
    try {
      await axios.post(`${API}/scholarships/${id}/archive`);
      toast.success('Scholarship archived');
      setDeleteDialog({ open: false, scholarship: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive scholarship');
    }
  };

  const unarchiveScholarship = async (id) => {
    try {
      await axios.post(`${API}/scholarships/${id}/unarchive`);
      toast.success('Scholarship restored from archive');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore scholarship');
    }
  };

  const duplicateScholarship = (scholarship) => {
    const today = new Date().toISOString().split('T')[0];
    setEditTab('details');
    setScholarshipForm({
      name: `${scholarship.name} (Copy)`,
      description: scholarship.description,
      amount: scholarship.amount,
      start_date: today,
      deadline: scholarship.deadline?.length === 10 ? `${scholarship.deadline}T23:59` : (scholarship.deadline || ''),
      eligibility: scholarship.eligibility,
      scholarship_type: scholarship.scholarship_type,
      scoring_criteria: scholarship.scoring_criteria.map(c => ({ ...c })),
      form_config: scholarship.form_config ? JSON.parse(JSON.stringify(scholarship.form_config)) : DEFAULT_FORM_CONFIG,
      is_active: false, // Start as inactive so admin can review before activating
      is_archived: false,
      allow_previous_recipients: scholarship.allow_previous_recipients !== false,
      scoring_template: scholarship.scoring_template ? JSON.parse(JSON.stringify(scholarship.scoring_template)) : { sections: [] }
    });
    setScholarshipDialog({ open: true, editing: null }); // null means create new
    toast.info('Scholarship duplicated - edit the name and details, then save');
  };

  const inviteUser = async () => {
    try {
      const response = await axios.post(`${API}/admin/invite-user`, inviteForm);
      toast.success(`Invitation sent! Token: ${response.data.token}`);
      setInviteDialog(false);
      setInviteForm({ email: '', first_name: '', last_name: '', role: 'chair' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const updateCriterion = (index, field, value) => {
    const newCriteria = [...scholarshipForm.scoring_criteria];
    newCriteria[index] = { ...newCriteria[index], [field]: value };
    setScholarshipForm({ ...scholarshipForm, scoring_criteria: newCriteria });
  };

  const addCriterion = () => {
    setScholarshipForm({
      ...scholarshipForm,
      scoring_criteria: [
        ...scholarshipForm.scoring_criteria,
        { name: '', weight: 0.1, max_score: 10, description: '' }
      ]
    });
  };

  const removeCriterion = (index) => {
    setScholarshipForm({
      ...scholarshipForm,
      scoring_criteria: scholarshipForm.scoring_criteria.filter((_, i) => i !== index)
    });
  };

  // Committee Functions
  const openCommitteeDialog = (committee = null) => {
    if (committee) {
      setCommitteeForm({
        name: committee.name,
        description: committee.description || '',
        level: committee.level || 'chapter',
        slug: committee.slug || ''
      });
      setCommitteeDialog({ open: true, editing: committee.id });
    } else {
      setCommitteeForm({ name: '', description: '', level: 'chapter', slug: '' });
      setCommitteeDialog({ open: true, editing: null });
    }
  };

  const saveCommittee = async () => {
    if (!committeeForm.slug) {
      toast.error('Committee slug is required');
      return;
    }
    try {
      if (committeeDialog.editing) {
        await axios.put(`${API}/committees/${committeeDialog.editing}`, committeeForm);
        toast.success('Committee updated');
      } else {
        await axios.post(`${API}/committees`, committeeForm);
        toast.success('Committee created');
      }
      setCommitteeDialog({ open: false, editing: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save committee');
    }
  };

  const inviteToCommittee = async () => {
    try {
      const response = await axios.post(
        `${API}/committees/${committeeInviteDialog.committee.id}/invite`,
        committeeInviteForm
      );
      toast.success(`Invitation sent to ${committeeInviteForm.email}`);
      if (response.data.email_sent) {
        toast.info('Email notification sent');
      }
      setCommitteeInviteDialog({ open: false, committee: null });
      setCommitteeInviteForm({ email: '', first_name: '', last_name: '', role: 'chair' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const removeCommitteeMember = async (committeeId, userId, firstName, lastName) => {
    setConfirmDialog({
      open: true,
      type: 'remove_member',
      data: { committeeId, userId, firstName, lastName },
      message: `Are you sure you want to remove ${firstName} ${lastName} from this committee?`
    });
  };

  const deleteCommittee = async (committeeId, committeeName) => {
    setConfirmDialog({
      open: true,
      type: 'delete_committee',
      data: { committeeId, committeeName },
      message: `Are you sure you want to delete "${committeeName}"? This will remove all members and cannot be undone.`
    });
  };

  const handleConfirmAction = async () => {
    const { type, data } = confirmDialog;
    setConfirmDialog({ open: false, type: null, data: null, message: '' });
    
    try {
      const token = localStorage.getItem('token');
      
      if (type === 'remove_member') {
        await axios.delete(`${API}/committees/${data.committeeId}/members/${data.userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`${data.firstName} ${data.lastName} has been removed from the committee`);
      } else if (type === 'delete_committee') {
        await axios.delete(`${API}/committees/${data.committeeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Committee "${data.committeeName}" has been deleted`);
      } else if (type === 'bulk_delete_users') {
        await axios.post(`${API}/admin/users/bulk-delete`, { user_ids: data.userIds }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`${data.userIds.length} user(s) have been deleted`);
        setSelectedUsers([]);
      } else if (type === 'bulk_archive_users') {
        await axios.post(`${API}/admin/users/bulk-archive`, { user_ids: data.userIds }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`${data.userIds.length} user(s) have been archived`);
        setSelectedUsers([]);
      }
      
      fetchData();
    } catch (error) {
      console.error('Action error:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete action');
    }
  };

  // User bulk actions
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAllUsers = () => {
    const nonAdminUsers = users.filter(u => u.role !== 'admin');
    if (selectedUsers.length === nonAdminUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(nonAdminUsers.map(u => u.id));
    }
  };

  const bulkDeleteUsers = () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to delete');
      return;
    }
    setConfirmDialog({
      open: true,
      type: 'bulk_delete_users',
      data: { userIds: selectedUsers },
      message: `Are you sure you want to delete ${selectedUsers.length} user(s)? This action cannot be undone.`
    });
  };

  const bulkArchiveUsers = () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to archive');
      return;
    }
    setConfirmDialog({
      open: true,
      type: 'bulk_archive_users',
      data: { userIds: selectedUsers },
      message: `Are you sure you want to archive ${selectedUsers.length} user(s)? They will no longer be able to log in.`
    });
  };

  const getRoleDisplay = (role) => {
    const roles = {
      'chair': 'Chair',
      'co_chair': 'Co-Chair',
      'member': 'Member'
    };
    return roles[role] || role;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-greek-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/1k2v2h81_AHEP_CA_LOGO.jfif" 
                alt="AHEPA Logo" 
                className="w-10 h-10 object-contain rounded-full"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/gl3qcnxw_DOP_LOGO_LARGE.jfif" 
                alt="Daughters of Penelope Logo" 
                className="w-10 h-10 object-contain rounded-full"
              />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold" style={{ color: '#0D5EAF' }}>AHEPA Family</h1>
              <p className="text-xs text-slate-500">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-semibold text-slate-900">{user?.first_name} {user?.last_name}</p>
              <p className="text-sm text-slate-500">Administrator</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="rounded-sm border-slate-300"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white border border-slate-200 p-1 rounded-sm">
            <TabsTrigger value="overview" className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white" data-testid="tab-overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="committees" className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white" data-testid="tab-committees">
              <Building2 className="w-4 h-4 mr-2" />
              Committees
            </TabsTrigger>
            <TabsTrigger value="scholarships" className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white" data-testid="tab-scholarships">
              <Award className="w-4 h-4 mr-2" />
              Scholarships
            </TabsTrigger>
            <TabsTrigger value="applications" className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white" data-testid="tab-applications">
              <FileText className="w-4 h-4 mr-2" />
              Applications
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="templates" className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white" data-testid="tab-templates">
              <Palette className="w-4 h-4 mr-2" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <AdminOverviewTab
              statistics={statistics}
              setActiveTab={setActiveTab}
              handleImportPreviewData={handleImportPreviewData}
              importingData={importingData}
            />
          </TabsContent>

          {/* Committees Tab */}
          <TabsContent value="committees">
            <AdminCommitteesTab
              committees={committees}
              openCommitteeDialog={openCommitteeDialog}
              setCommitteeMemberLimits={setCommitteeMemberLimits}
              setCommitteeInviteForm={setCommitteeInviteForm}
              setCommitteeInviteDialog={setCommitteeInviteDialog}
              deleteCommittee={deleteCommittee}
              removeCommitteeMember={removeCommitteeMember}
              getRoleDisplay={getRoleDisplay}
            />
          </TabsContent>

          {/* Scholarships Tab */}
          <TabsContent value="scholarships">
            <AdminScholarshipsTab
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              openScholarshipDialog={openScholarshipDialog}
              scholarships={scholarships}
              committees={committees}
              unarchiveScholarship={unarchiveScholarship}
              duplicateScholarship={duplicateScholarship}
              archiveScholarship={archiveScholarship}
              deleteScholarship={deleteScholarship}
            />
          </TabsContent>
          <TabsContent value="users">
            <AdminUsersTab
              users={users}
              selectedUsers={selectedUsers}
              toggleSelectAllUsers={toggleSelectAllUsers}
              toggleUserSelection={toggleUserSelection}
              bulkArchiveUsers={bulkArchiveUsers}
              bulkDeleteUsers={bulkDeleteUsers}
              setInviteDialog={setInviteDialog}
            />
          </TabsContent>
          <TabsContent value="applications">
            <AdminApplicationsTab
              allApplications={allApplications}
              committees={committees}
              applicationFilter={applicationFilter}
              setApplicationFilter={setApplicationFilter}
              viewingApplication={viewingApplication}
              setViewingApplication={setViewingApplication}
              scholarships={scholarships}
              onScoreSubmit={fetchData}
              setActiveTab={setActiveTab}
            />
          </TabsContent>
          <TabsContent value="templates">
            <TemplateManager isAdmin={true} />
          </TabsContent>
          
          {/* Settings Tab */}
          <TabsContent value="settings">
            <AdminSettingsTab
              scholarshipTypes={scholarshipTypes}
              newScholarshipType={newScholarshipType}
              setNewScholarshipType={setNewScholarshipType}
              addScholarshipType={addScholarshipType}
              removeScholarshipType={removeScholarshipType}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Application Scoring Modal removed - now rendered inline in Applications tab */}
      {/* Scholarship Dialog */}
      <AdminScholarshipModal
        scholarshipDialog={scholarshipDialog}
        setScholarshipDialog={setScholarshipDialog}
        scholarshipForm={scholarshipForm}
        setScholarshipForm={setScholarshipForm}
        scholarships={scholarships}
        committees={committees}
        scholarshipTypes={scholarshipTypes}
        editTab={editTab}
        setEditTab={setEditTab}
        saveScholarship={saveScholarship}
        duplicateScholarship={duplicateScholarship}
        addCriterion={addCriterion}
        updateCriterion={updateCriterion}
        removeCriterion={removeCriterion}
        descriptionRef={descriptionRef}
        eligibilityRef={eligibilityRef}
        handleTextareaResize={handleTextareaResize}
      />

      {/* Invite User Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Invite User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={inviteForm.first_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                  className="rounded-sm"
                  data-testid="input-invite-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={inviteForm.last_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                  className="rounded-sm"
                  data-testid="input-invite-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                className="rounded-sm"
                data-testid="input-invite-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
              >
                <SelectTrigger className="rounded-sm" data-testid="select-invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="chair">Scholarship Chair</SelectItem>
                  <SelectItem value="committee">Committee Member</SelectItem>
                  <SelectItem value="sponsor">Sponsor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialog(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={inviteUser} className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm" data-testid="send-invite-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Delete Scholarship</DialogTitle>
            <DialogDescription>
              This scholarship has applications associated with it. Choose how to proceed:
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
              <p className="text-sm text-amber-800">
                <strong>Recommended:</strong> Archive the scholarship instead of deleting. This preserves all application data and allows you to restore it later.
              </p>
            </div>
            <div className="p-4 bg-red-50 border border-red-200 rounded-sm">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> Permanently deleting will remove the scholarship record. Applications will remain but will reference a deleted scholarship.
              </p>
            </div>
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, scholarship: null })} 
              className="rounded-sm"
            >
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button 
                onClick={() => archiveScholarship(deleteDialog.scholarship?.id)}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
                data-testid="archive-confirm-btn"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archive
              </Button>
              <Button 
                onClick={() => deleteScholarship(deleteDialog.scholarship?.id, true)}
                className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
                data-testid="delete-confirm-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Permanently
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Committee Dialog */}
      <Dialog open={committeeDialog.open} onOpenChange={(open) => setCommitteeDialog({ open, editing: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{committeeDialog.editing ? 'Edit Committee' : 'Create Committee'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Committee Name</Label>
              <Input
                value={committeeForm.name}
                onChange={(e) => setCommitteeForm({ ...committeeForm, name: e.target.value })}
                placeholder="e.g., Ottawa Chapter"
                data-testid="committee-name-input"
              />
            </div>

            <div>
              <Label>Landing Page Slug</Label>
              <Input
                value={committeeForm.slug}
                onChange={(e) => setCommitteeForm({ ...committeeForm, slug: e.target.value })}
                placeholder="e.g., ottawa"
                data-testid="committee-slug-input"
              />
              <p className="text-xs text-slate-500 mt-1" data-testid="committee-slug-preview">
                {committeeForm.slug ? `${landingBaseUrl}/${committeeForm.slug}` : 'Set a slug to generate the landing page URL.'}
              </p>
            </div>
            
            <div>
              <Label>Committee Level</Label>
              <Select
                value={committeeForm.level}
                onValueChange={(value) => setCommitteeForm({ ...committeeForm, level: value })}
              >
                <SelectTrigger className="rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="national">National</SelectItem>
                  <SelectItem value="district">District</SelectItem>
                  <SelectItem value="chapter">Chapter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={committeeForm.description}
                onChange={(e) => setCommitteeForm({ ...committeeForm, description: e.target.value })}
                placeholder="Brief description of the committee"
                rows={3}
                data-testid="committee-description-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommitteeDialog({ open: false, editing: null })}>
              Cancel
            </Button>
            <Button
              onClick={saveCommittee}
              className="bg-greek-blue hover:bg-blue-700"
              disabled={!committeeForm.name || !committeeForm.slug}
              data-testid="save-committee-btn"
            >
              {committeeDialog.editing ? 'Save Changes' : 'Create Committee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Committee Invite Dialog */}
      <Dialog open={committeeInviteDialog.open} onOpenChange={(open) => setCommitteeInviteDialog({ open, committee: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite to {committeeInviteDialog.committee?.name}</DialogTitle>
            <DialogDescription>
              Send an invitation to join this committee
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input
                  value={committeeInviteForm.first_name}
                  onChange={(e) => setCommitteeInviteForm({ ...committeeInviteForm, first_name: e.target.value })}
                  placeholder="John"
                  data-testid="committee-invite-first-name"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={committeeInviteForm.last_name}
                  onChange={(e) => setCommitteeInviteForm({ ...committeeInviteForm, last_name: e.target.value })}
                  placeholder="Doe"
                  data-testid="committee-invite-last-name"
                />
              </div>
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={committeeInviteForm.email}
                onChange={(e) => setCommitteeInviteForm({ ...committeeInviteForm, email: e.target.value })}
                placeholder="john@example.com"
                data-testid="committee-invite-email"
              />
            </div>
            
            <div>
              <Label>Role</Label>
              <Select
                value={committeeInviteForm.role}
                onValueChange={(value) => setCommitteeInviteForm({ ...committeeInviteForm, role: value })}
              >
                <SelectTrigger data-testid="committee-invite-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem 
                    value="chair"
                    disabled={committeeMemberLimits && committeeMemberLimits.available.chair <= 0}
                  >
                    Chair {committeeMemberLimits && `(${committeeMemberLimits.available.chair} of ${committeeMemberLimits.limits.chair} available)`}
                  </SelectItem>
                  <SelectItem 
                    value="co_chair"
                    disabled={committeeMemberLimits && committeeMemberLimits.available.co_chair <= 0}
                  >
                    Co-Chair {committeeMemberLimits && `(${committeeMemberLimits.available.co_chair} of ${committeeMemberLimits.limits.co_chair} available)`}
                  </SelectItem>
                  <SelectItem 
                    value="member"
                    disabled={committeeMemberLimits && committeeMemberLimits.available.member <= 0}
                  >
                    Committee Member {committeeMemberLimits && `(${committeeMemberLimits.available.member} of ${committeeMemberLimits.limits.member} available)`}
                  </SelectItem>
                </SelectContent>
              </Select>
              {committeeMemberLimits && (
                <p className="text-xs text-slate-500 mt-1">
                  Current: {committeeMemberLimits.current.chair} Chair(s), {committeeMemberLimits.current.co_chair} Co-Chair(s), {committeeMemberLimits.current.member} Member(s)
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommitteeInviteDialog({ open: false, committee: null })}>
              Cancel
            </Button>
            <Button
              onClick={inviteToCommittee}
              className="bg-greek-blue hover:bg-blue-700"
              disabled={!committeeInviteForm.email || !committeeInviteForm.first_name || !committeeInviteForm.last_name ||
                (committeeMemberLimits && committeeMemberLimits.available[committeeInviteForm.role] <= 0)}
              data-testid="send-committee-invite-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, type: null, data: null, message: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirm Action
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-slate-600 py-4">{confirmDialog.message}</p>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setConfirmDialog({ open: false, type: null, data: null, message: '' })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              className={confirmDialog.type?.includes('archive') ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
              data-testid="confirm-action-btn"
            >
              {confirmDialog.type?.includes('archive') ? (
                <>
                  <Archive className="w-4 h-4 mr-2" />
                  Yes, Archive
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Yes, Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
